import { useEffect } from 'react';
import { cn } from '@/lib/utils';

interface SlideOverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  actions?: React.ReactNode;
}

interface SlideOverHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function SlideOver({
  open,
  onOpenChange,
  children,
  title,
  description,
  actions,
}: SlideOverProps) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={() => onOpenChange(false)} />
      <div className="fixed inset-y-0 right-0 z-50 w-[60vw] shadow-xl flex flex-col bg-background border-l animate-in slide-in-from-right duration-300">
        <div className="h-16 border-b bg-card/95 backdrop-blur-sm flex items-center justify-between px-6 shrink-0 overflow-hidden">
          <div className="flex-1 min-w-0 mr-4">
            {title && <h2 className="text-lg font-semibold truncate">{title}</h2>}
            {description && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {actions}
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-background">{children}</div>
      </div>
    </>
  );
}

export function SlideOverHeader({ children, className }: SlideOverHeaderProps) {
  return <div className={cn('mb-4', className)}>{children}</div>;
}

interface SlideOverContentProps {
  children: React.ReactNode;
  className?: string;
}

export function SlideOverContent({ children, className }: SlideOverContentProps) {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}
